/**
 * CE Quick-Add — Custom Element for product card add-to-cart.
 *
 * - Stop card-link navigation on button click (button-in-anchor pragma).
 * - POST /cart/add.js for the default variant.
 * - Refetch /cart.js + publish PUB_SUB_EVENTS.cartUpdate so the header
 *   badge + cart-drawer pick up the change (same pipeline as cart.js).
 * - State machine: idle → loading → success/error → idle (data-state attr).
 *
 * Retry-Loop für subscribe/publish, weil dieses Script potenziell vor
 * constants.js + global.js läuft — gleiches Muster wie ce-header.liquid
 * Cart-Badge-Listener.
 */
class CEQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.variantId = null;
    this.resetTimer = null;
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    this.variantId = this.dataset.variantId;
    if (!this.button || !this.variantId) return;
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  disconnectedCallback() {
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }

  onClick(event) {
    // Card ist ein <a> — Navigation unterbinden.
    event.preventDefault();
    event.stopPropagation();
    if (this.dataset.state === 'loading') return;
    this.add();
  }

  async add() {
    this.setState('loading');
    try {
      const res = await fetch(`${window.routes ? routes.cart_add_url : '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(this.variantId, 10), quantity: 1 }],
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || (data.status && data.status >= 400)) {
        // Shopify-Error-Format: {status, message, description} oder {errors}
        this.setState('error');
        this.scheduleReset(2200);
        // eslint-disable-next-line no-console
        console.warn('[quick-add] add failed', data);
        return;
      }

      // Cart-State holen + PubSub feuern, damit Header-Badge & Cart-Drawer aktualisieren.
      try {
        const cartRes = await fetch(`${window.routes ? routes.cart_url : '/cart'}.js`);
        const cart = await cartRes.json();
        this.publishCartUpdate(cart);
      } catch (e) {
        /* PubSub-Fehler nicht blockierend */
      }

      this.setState('success');
      this.scheduleReset(1500);
    } catch (e) {
      this.setState('error');
      this.scheduleReset(2200);
      // eslint-disable-next-line no-console
      console.warn('[quick-add] network error', e);
    }
  }

  setState(state) {
    this.dataset.state = state;
    if (this.button) {
      this.button.disabled = state === 'loading';
    }
  }

  scheduleReset(ms) {
    if (this.resetTimer) clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => this.setState('idle'), ms);
  }

  publishCartUpdate(cart) {
    let tries = 0;
    const tryFire = () => {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-add',
          cartData: cart,
          variantId: this.variantId,
        });
        return;
      }
      if (++tries > 50) return;
      setTimeout(tryFire, 100);
    };
    tryFire();
  }
}

if (!customElements.get('ce-quick-add')) {
  customElements.define('ce-quick-add', CEQuickAdd);
}
